import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ButtonModel, IBasicStyledProps, theme } from '@notino/react-styleguide-v20';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { FIELD_PRODUCT_ERROR, useValidationContext } from '@context/ValidationContext';
import { useMediaQuery, useNeedsScroll } from '@hooks';
import {
  EcommerceSectionNamespace,
  getButtonCheckoutInteraction,
  trackCheckoutButton,
  trackFormSubmitFailed,
  useTrackingTiming,
} from '@services/ecommerceService';
import { useRedirect } from '@services/routingService';
import { StickyFooterRow } from '@sharedComponents/StickyFooterRow';
import { EPage, ReactFCV } from '@types';
import { EScrollRegistryKeys, triggerRegisteredScroll } from '@utils/scrollRegistry';
import { CypressTestNamespace } from '@utils/test';

import { useCartPageContext } from '../../context';

import { useBeginCheckoutProcess } from './hooks/useBeginCheckoutProcess';
import { messages } from './messages';

export const CartPageButtonRowWrapper: ReactFCV<IBasicStyledProps> = (rest) => {
  // General hooks
  const redirectMethod = useRedirect();
  const { getErrorByField } = useValidationContext();
  const { formatMessage } = useIntl();
  const {
    state: {
      cartPageState,
      pricesSummary: { priceItemSummary },
    },
  } = useCartPageContext();

  const {
    applicationSettings: {
      features: { enableStickyButtonsOnCart },
    },
  } = useApplicationRuntimeContext();
  const getTiming = useTrackingTiming();

  // Sticky buttons hooks
  const desktopView = useMediaQuery(`(min-width: ${theme.breakpoints.sm}rem)`);
  const { canProceed, proceed } = useBeginCheckoutProcess();

  const { elementRef, needScroll } = useNeedsScroll();
  const threshold = desktopView ? 1 : 0.5;

  // redirect to home page callback
  const redirectBack = useCallback(
    (isSticky: boolean = false) => {
      const interaction = getButtonCheckoutInteraction(isSticky, needScroll);
      trackCheckoutButton(
        EcommerceSectionNamespace.EEcommerceName.BACK_TO_SHOPPING,
        EcommerceSectionNamespace.EEcommerceType.CHECKOUT_CART,
        interaction,
        getTiming(),
      );
      redirectMethod(EPage.HOME_PAGE);
    },
    [redirectMethod, getTiming, needScroll],
  );

  const onClickSecondButton = useCallback(() => {
    if (enableStickyButtonsOnCart) {
      const errorField = getErrorByField(FIELD_PRODUCT_ERROR);
      if (errorField) {
        trackFormSubmitFailed(
          errorField.message,
          EcommerceSectionNamespace.EEcommerceType.CHECKOUT_CART,
          errorField.field,
        );

        triggerRegisteredScroll([EScrollRegistryKeys.PRODUCT_ERROR_MESSAGE]);
        return;
      }
    }

    proceed(getButtonCheckoutInteraction(enableStickyButtonsOnCart, needScroll));
  }, [enableStickyButtonsOnCart, proceed, needScroll, getErrorByField]);

  // page is loaded (Loading state is 0) => false
  const pageLoaded = Boolean(cartPageState);

  // is sticky in case:
  // - page is already loaded
  // - sticky buttons ab test is on
  const isStickyAvailable = pageLoaded && enableStickyButtonsOnCart;

  return (
    <div ref={elementRef}>
      <StickyFooterRow
        data-testid="page-footer-row"
        isStickyAvailable={isStickyAvailable}
        threshold={threshold}
        price={priceItemSummary}
        firstButtonProps={{
          text: formatMessage(messages.continueShoppingButtonText),
          isVisible: true,
          onClickAction: () => redirectBack(false),
          className: CypressTestNamespace.ECartPageButtons.BACK_TO_STORE_BUTTON,
          dataTestId: 'first-footer-row-btn',
        }}
        stickyFirstButtonPropsOverride={{
          isVisible: desktopView,
          dataTestId: 'first-footer-row-btn-sticky',
          onClickAction: () => redirectBack(true),
        }}
        secondButtonProps={{
          text: formatMessage(messages.proceedToCheckoutButtonText),
          isVisible: true,
          onClickAction: onClickSecondButton,
          className: CypressTestNamespace.ECartPageButtons.TO_CHECKOUT_BUTTON,
          dataTestId: 'second-footer-row-btn',
          disabled: enableStickyButtonsOnCart ? false : !canProceed,
        }}
        stickySecondButtonPropsOverride={{
          onClickAction: onClickSecondButton,
          dataTestId: 'second-footer-row-btn-sticky',
        }}
        buttonsSize={ButtonModel.Sizes.default}
        mobileButtonOrder={true}
        {...rest}
      />
    </div>
  );
};
